import * as THREE from "three";
import * as T from "../textures.js";

/**
 * The street's palette, taken off the reference photographs: beige brick,
 * charcoal shingle, cream trim, and the mix of asphalt and pale concrete
 * driveways that gives a row of near-identical houses its variety.
 *
 * Materials are shared and merged per-material, so the whole neighbourhood
 * still draws in a couple of dozen calls.  Add a colour here rather than
 * making a one-off material in a level module, or the draw count creeps.
 */
const mk = (name, color, opts = {}) => {
  const params = {
    name,
    color,
    roughness: opts.roughness ?? 0.9,
    metalness: opts.metalness ?? 0,
    vertexColors: true,
    map: opts.map ? opts.map() : null,
    normalMap: opts.normalMap ? opts.normalMap() : null,
    // A cutout, not a fade.  These two together are how wire mesh gets drawn:
    // the piece stays in the opaque pass, so it needs no sorting and keeps the
    // castShadow that Builder.finish gives every opaque batch -- and since
    // three copies alphaMap and alphaTest onto the depth material it renders
    // the shadow pass with (WebGLShadowMap.getDepthMaterial), the shadow comes
    // out with the holes in it instead of as a solid rectangle on the grass.
    // opts.transparent is the other answer and costs both of those.
    alphaMap: opts.alphaMap ? opts.alphaMap() : null,
    alphaTest: opts.alphaTest ?? 0,
    emissive: opts.emissive ?? 0x000000,
    transparent: opts.transparent ?? false,
    opacity: opts.opacity ?? 1,
    depthWrite: opts.depthWrite ?? true,
    // Only reaches the shader for a material that carries its own envMap: the
    // renderer overwrites this uniform with scene.environmentIntensity for
    // everything that rides on scene.environment instead.  See useEnvironment.
    envMapIntensity: opts.envMapIntensity ?? 1,
    side: opts.side ?? THREE.FrontSide,
  };
  // Clearcoat exists only on MeshPhysicalMaterial, so the class and the extra
  // keys have to move together -- handing an unknown key to the Standard
  // constructor is a console warning for every material that does it, and the
  // property silently does nothing.
  const m = opts.clearcoat
    ? new THREE.MeshPhysicalMaterial({
        ...params,
        clearcoat: opts.clearcoat,
        clearcoatRoughness: opts.clearcoatRoughness ?? 0.1,
        clearcoatNormalMap: opts.clearcoatNormalMap ? opts.clearcoatNormalMap() : null,
      })
    : new THREE.MeshStandardMaterial(params);

  if (m.map && opts.repeat) m.map.repeat.set(opts.repeat, opts.repeat);
  if (m.normalMap) {
    m.normalScale.setScalar(opts.normalScale ?? 1);
    if (opts.normalRepeat) m.normalMap.repeat.setScalar(opts.normalRepeat);
  }
  if (m.clearcoatNormalMap) {
    m.clearcoatNormalScale.setScalar(opts.clearcoatNormalScale ?? 1);
    if (opts.clearcoatNormalRepeat) {
      m.clearcoatNormalMap.repeat.setScalar(opts.clearcoatNormalRepeat);
    }
  }
  return m;
};

export const MATS = {
  // --- buildings ---
  brick:      mk("brick", 0xc6b291, { map: T.brick, roughness: 0.95 }),
  brickWarm:  mk("brickWarm", 0xc3a882, { map: T.brick, roughness: 0.95 }),
  brickPale:  mk("brickPale", 0xd0c1a4, { map: T.brick, roughness: 0.95 }),
  stoneTrim:  mk("stoneTrim", 0xbdb4a2, { map: T.concrete, roughness: 0.9 }),
  shingle:    mk("shingle", 0x4c5057, { map: T.shingle, roughness: 0.95 }),
  shingleAlt: mk("shingleAlt", 0x565a61, { map: T.shingle, roughness: 0.95 }),
  trim:       mk("trim", 0xefe9dc, { roughness: 0.72 }),
  trimShade:  mk("trimShade", 0xdcd5c6, { roughness: 0.72 }),
  garageDoor: mk("garageDoor", 0xe6e1d4, { map: T.siding, roughness: 0.7 }),
  glass:      mk("glass", 0x2c3a45, { roughness: 0.16, metalness: 0.1 }),
  doorRed:    mk("doorRed", 0x8b3229, { roughness: 0.6 }),
  doorTan:    mk("doorTan", 0xa87d4e, { roughness: 0.6 }),
  doorGreen:  mk("doorGreen", 0x3f5c47, { roughness: 0.6 }),

  // --- ground surfaces ---
  asphalt:    mk("asphalt", 0x4a4b4e, { map: T.asphalt, roughness: 0.97 }),
  roadTop:    mk("roadTop", 0x55565a, { map: T.asphalt, roughness: 0.97 }),
  concrete:   mk("concrete", 0xc2bcb0, { map: T.concrete, roughness: 0.94 }),
  concreteDark: mk("concreteDark", 0xa9a396, { map: T.concrete, roughness: 0.94 }),
  paver:      mk("paver", 0xb0a897, { map: T.paver, roughness: 0.92 }),
  lineWhite:  mk("lineWhite", 0xd8d5cb, { roughness: 0.85 }),
  lineYellow: mk("lineYellow", 0xc9a63c, { roughness: 0.85 }),

  // --- planting ---
  lawn:       mk("lawn", 0x76a04c, { map: T.turf, roughness: 0.96, repeat: 24 }),
  lawnDry:    mk("lawnDry", 0x8aa055, { map: T.turf, roughness: 0.96, repeat: 24 }),
  mulch:      mk("mulch", 0x402c1d, { roughness: 0.98 }),
  soil:       mk("soil", 0x7d6748, { map: T.turf, roughness: 0.98, repeat: 20 }),
  hedge:      mk("hedge", 0x406b36, { roughness: 0.97 }),
  leaf:       mk("leaf", 0x53823c, { roughness: 0.95 }),
  leafDark:   mk("leafDark", 0x416930, { roughness: 0.95 }),
  leafWarm:   mk("leafWarm", 0x6d8f3a, { roughness: 0.95 }),
  bark:       mk("bark", 0x7a5c3e, { map: T.bark, roughness: 0.96 }),
  barkDark:   mk("barkDark", 0x604a34, { roughness: 0.96 }),
  // The burr bush (greenspace.js burrBush -- the ONE plant that snags).  Its
  // whole job is to be tellable from every green in this palette at a
  // distance, at cat's-eye height, by a child: a dried, rusty ochre that
  // nothing alive here wears.  Deliberately NOT the browns above -- bark is
  // vertical and trunk-shaped where this is a dome, but at dusk a colour
  // does more work than a shape, so it sits well away from bark's leathery
  // brown too, pushed toward orange.  0x8a5c28 rather than the 0x9a6a2f it
  // was first mixed at: under the warm key light and the per-piece tint the
  // brighter mix rendered closer to a pumpkin than a dead plant, and the
  // deeper rust reads just as loudly against the greens while staying
  // botanical (checked side by side in the greenspace screenshot pass).
  // Rougher than the living foliage because dead stems scatter, not sheen.
  burr:       mk("burr", 0x8a5c28, { roughness: 0.98 }),
  // The seed heads: near-black knobs held above the foliage on stalks, the
  // silhouette half of the same warning (the colour half is `burr`).  Dark
  // enough to read against sky, grass and mulch alike -- the one background
  // it must never vanish into is the bush under it.
  burrHead:   mk("burrHead", 0x33241a, { roughness: 0.9 }),

  // --- the creek ---
  /**
   * Shallow water standing in a soil channel.
   *
   * Emphatically not `glass`, which is the nine window panes along the street:
   * laying that flat in the grass is exactly what made the creek read as a row
   * of blue rectangles, and retuning it to fix the creek would have taken every
   * pane with it.
   *
   * The tint has been through three rounds.  It began as the olive a
   * tea-coloured creek gets from fifteen centimetres over a soil bed --
   * physically honest, and too drab to read as water in play.  Round two
   * pushed it to a blue-teal (0x2b4d63) and the creek STILL came out brown,
   * which is the lesson the current numbers are built on: at 0.7 opacity
   * nearly a third of every pixel was the sunlit soil and shingle behind the
   * surface, and no base colour survives that much bright warm brown blended
   * into it -- retinting the water alone only ever moved WHICH brown.  So the
   * fix is split three ways.  The bed below the waterline is lined dark with
   * wetSoil now (greenspace's wetBed), so what bleeds through is dim and
   * close to neutral; opacity is up to 0.8, which keeps more of the water's
   * own answer -- reflection included -- in the pixel while still letting the
   * drowned stones read; and with the brown out of the blend the base could
   * finally move to an honest slate blue instead of a teal over-corrected
   * against contamination.  Dark and desaturated rather than a pool cyan even
   * so: this is a creek in a wood under a warm key light, and most of its
   * blue still arrives off the sky.
   *
   * Roughness is still the number that matters most: water is legible almost
   * entirely from what it reflects, so this is low enough for the environment
   * map to land sharply on it, and the ripple normals are what stop that
   * reflection being a mirror.
   *
   * envMapIntensity is 0.35, and round FOUR is what put it there.  At 1.2 the
   * creek still read pale and silver rather than blue, and the cause is
   * Fresnel doing exactly what it should.  A dielectric reflects about 4% of
   * what is in front of it head-on and very nearly 100% at a glancing one,
   * and the diffuse term it would otherwise show is attenuated by the same
   * curve -- so the blue survives only in proportion to how steeply you are
   * looking down.  The camera in this game rides just behind a cat 0.32 m at
   * the shoulder, which means almost every pixel of creek anyone ever sees is
   * a GLANCING pixel.  Worse, a glancing reflection samples the environment
   * near the HORIZON, and ENVIRONMENT's horizon stops (#b9cfdc, #bccfd8) are
   * its palest, most desaturated band by design.  So the surface was faithfully
   * mirroring the whitest part of the sky across the whole of its visible area.
   *
   * Confirmed rather than guessed: driving envMapIntensity to 0 in a live
   * build turns the creek a strong saturated blue, and dropping the clearcoat
   * to 0 while leaving the intensity alone changes almost nothing -- so the
   * second specular lobe was never the problem and the base layer's own
   * environment term was all of it.  0.35 is the value where the water is
   * unambiguously blue from a cat's-eye camera and still carries a moving
   * silver ripple highlight; the reflection is once again a highlight ON the
   * water rather than the water itself.  Physically it is an understatement,
   * and deliberately: a real creek at this angle IS a mirror, but a mirror is
   * not what anyone means when they ask for blue water.
   *
   * TWO ripple maps, and this is why the material is Physical rather than
   * Standard.  A single scrolling normal map does not read as water: every
   * ripple keeps its shape and its neighbours as it travels, so the eye sees a
   * picture of water being slid along the creek.  Two fields crossing at
   * different scales and headings interfere, and ripples appear, cross and go
   * with no period to lock onto.  Standard has one normal slot; the clearcoat
   * gives the second field a slot of its own AND its own specular lobe, which
   * is close to what a water surface physically is -- a smooth reflecting skin
   * over a murky body.
   *
   * depthWrite stays ON.  The creek is laid as overlapping coplanar segments,
   * and with depth writing off every overlap blends twice and stripes the
   * surface at the segment pitch; with it on the nearer segment simply wins.
   */
  water: mk("water", 0x2e6191, {
    roughness: 0.08, metalness: 0,
    transparent: true, opacity: 0.8, depthWrite: true,
    normalMap: () => T.ripples(0), normalScale: 0.55, normalRepeat: 1.2,
    clearcoat: 1, clearcoatRoughness: 0.05,
    clearcoatNormalMap: () => T.ripples(1),
    clearcoatNormalScale: 0.35, clearcoatNormalRepeat: 0.55,
    envMapIntensity: 0.35,
  }),
  // The soaked bed.  greenspace's wetBed lines the whole channel below the
  // waterline with this, and its colour is doing water duty as much as soil
  // duty: a fifth of it shows through every water pixel, so it is dark and
  // near-neutral where `soil` is bright and warm -- the difference between a
  // blue creek and an olive one lives as much here as in `water` above.
  // Damp soil is not just a darker soil either -- it is much less rough,
  // which is the whole reason it reads as wet, so the bed in the shallows
  // catches a sheen that `soil` never can.
  wetSoil:    mk("wetSoil", 0x4a3f2e, { map: T.stone, roughness: 0.6, repeat: 14 }),

  // --- fences, timber, props ---
  fence:      mk("fence", 0x9c7a4e, { map: T.planks, roughness: 0.93 }),
  fenceGrey:  mk("fenceGrey", 0x8d8171, { map: T.planks, roughness: 0.93 }),
  timber:     mk("timber", 0x8a6a44, { map: T.planks, roughness: 0.9 }),
  metalDark:  mk("metalDark", 0x3a3d42, { roughness: 0.45, metalness: 0.5 }),
  // The rear boundary's fabric.  Darker than metalDark, which is the posts and
  // rails it hangs on: a wire seen against the sky is mostly its own shadowed
  // underside, so fabric that matches its frame reads as a painted panel.
  // Rougher and less metallic too -- this is the powder-coated stuff, not the
  // galvanised.  See textures.chainLink for the mask and the 0.25 cut.
  chainLink:  mk("chainLink", 0x24272b, {
    alphaMap: T.chainLink, alphaTest: 0.25, roughness: 0.62, metalness: 0.3,
  }),
  metalPale:  mk("metalPale", 0x9aa0a6, { roughness: 0.4, metalness: 0.55 }),
  plasticGreen: mk("plasticGreen", 0x35603a, { roughness: 0.55 }),
  plasticBlue:  mk("plasticBlue", 0x2c4d78, { roughness: 0.55 }),
  plasticGrey:  mk("plasticGrey", 0x5a5f63, { roughness: 0.55 }),
  // The parked car's sheet metal.  Charcoal on purpose: the vendored model
  // this prop used to be was repainted from its download red to charcoal
  // before it ever shipped, and the box car that replaced it (client car.js,
  // drawn from spec.js's CAR_TIERS) keeps the colour the street already
  // knows.  Clearcoat because car paint is a smooth skin over a duller base
  // -- the second lobe is what reads as "painted" rather than "plastic"
  // when the sky lands on the bonnet.
  // 0x4d545e rather than the 0x33373c it was first mixed at: under the AO
  // fade every box carries, the darker mix rendered the same near-black as
  // the glass beside it and the car read as one unbroken silhouette
  // (checked in the driveway screenshot pass).  This sits in the shingle
  // family -- the street's established charcoal -- and leaves the canopy
  // legibly darker.
  carPaint:   mk("carPaint", 0x4d545e, {
    roughness: 0.42, metalness: 0.15, clearcoat: 0.7, clearcoatRoughness: 0.15,
  }),
  // Named for the SUV it was first mixed for.  It has been the patio table's
  // smoked glass since that car became a model; now the car is boxes again
  // (car.js) and its canopy -- windscreen, roof, back glass -- wears it too,
  // the same dark sheet on both.
  carGlass:   mk("carGlass", 0x1b2228, { roughness: 0.12, metalness: 0.2 }),
  // The wheel band under the car's sills: rubber, so rough and off-black --
  // darker than the charcoal paint above it, no sheen to speak of.
  tyre:       mk("tyre", 0x1a1b1d, { roughness: 0.95 }),
  flowerWarm: mk("flowerWarm", 0xd8734a, { roughness: 0.7 }),
  flowerPale: mk("flowerPale", 0xe8dcb8, { roughness: 0.7 }),
  stone:      mk("stone", 0xb3aa9b, { map: T.stone, roughness: 0.92 }),

  // --- hero back garden, matched to the reference photograph ---
  shedBlue:   mk("shedBlue", 0x93a8b9, { map: T.siding, roughness: 0.86 }),
  shedShadow: mk("shedShadow", 0x7d92a4, { map: T.siding, roughness: 0.86 }),
  deckGrey:   mk("deckGrey", 0x9d9287, { map: T.planks, roughness: 0.94 }),
  deckShade:  mk("deckShade", 0x877d73, { map: T.planks, roughness: 0.94 }),
  flagstone:  mk("flagstone", 0xc0b09a, { map: T.concrete, roughness: 0.93 }),
  sod:        mk("sod", 0x6f9c3e, { map: T.turf, roughness: 0.96, repeat: 24 }),
  sodSeam:    mk("sodSeam", 0x638c37, { map: T.turf, roughness: 0.96, repeat: 24 }),
  conifer:    mk("conifer", 0x2f5334, { roughness: 0.96 }),
  coniferLit: mk("coniferLit", 0x3d6a3c, { roughness: 0.96 }),
  acerRed:    mk("acerRed", 0x8c3b34, { roughness: 0.93 }),
  boxwood:    mk("boxwood", 0x5c8a3a, { roughness: 0.95 }),
  edgePaver:  mk("edgePaver", 0xb99570, { map: T.paver, roughness: 0.92 }),
  hoseOrange: mk("hoseOrange", 0xc9682c, { roughness: 0.6 }),
  canopyGreen: mk("canopyGreen", 0x8fae55, { roughness: 0.78 }),
  toyRed:     mk("toyRed", 0xb8412f, { roughness: 0.55 }),
  toyYellow:  mk("toyYellow", 0xd9a72c, { roughness: 0.55 }),
  toyBlue:    mk("toyBlue", 0x3a6ea8, { roughness: 0.55 }),
  toyCream:   mk("toyCream", 0xd8cba6, { roughness: 0.6 }),

  // --- inside the hero house ---
  // Interiors are lit only by the hemisphere fill and the bounce light (the
  // sun is outside the roof), so these run paler than they would in daylight
  // -- a true white plaster reads grey once it is indoors.
  plaster:     mk("plaster", 0xf2ece0, { roughness: 0.88 }),
  // Ceilings and soffits carry their own light.  The scene's fill is a
  // hemisphere whose lower half is lawn-coloured, which is right for the
  // underside of a porch roof and very wrong for a ceiling: a downward face
  // indoors gets pure 0x74854f and the room ends up with an olive lid.  A
  // near-black base with a neutral emissive leaves the hemisphere only a few
  // percent of the result, so a ceiling reads as a ceiling.
  ceiling:     mk("ceiling", 0x2b2c2a, { roughness: 0.95, emissive: 0xb9b6ac }),
  // Seen from a room, a window is a bright hole, not the dark pane the street
  // sees: `glass` is opaque, so the inside face gets its own daylit colour.
  glassLit:    mk("glassLit", 0xdfeaf0, { roughness: 0.35 }),
  floorBoard:  mk("floorBoard", 0xb98f5c, { map: T.planks, roughness: 0.8 }),
  floorShade:  mk("floorShade", 0xa47c4e, { map: T.planks, roughness: 0.8 }),
};

// ---------------------------------------------------------------------------
/**
 * Scroll the two ripple fields against each other.  Called per frame.
 *
 * Speeds are UV per second, and at normalRepeat 1.2 over the Builder's world
 * projection each tile covers roughly a metre, so this is a few centimetres a
 * second -- a creek, not a millrace.  The headings differ AND oppose along u:
 * two fields drifting the same way just look like one field drifting, however
 * different their scales.
 *
 * Set absolutely from an accumulated clock rather than incremented, which
 * matters because a level module can register its own updater over the same two
 * textures.  Incrementing would have the two drivers add up into whatever
 * combined speed the frame happened to produce; assigning makes the last writer
 * win, and main.js runs this after level.update, so this is it.
 *
 * The clock wraps at 1000 s.  Left running, a float32 offset in the thousands
 * has no bits left for the fraction the ripples actually live in: they quantise
 * into visible steps and then stop moving altogether.
 */
let waterClock = 0;
export function tickWater(dt) {
  waterClock = (waterClock + dt) % 1000;
  const a = MATS.water.normalMap, b = MATS.water.clearcoatNormalMap;
  if (a) a.offset.set(waterClock * 0.021, waterClock * 0.013);
  if (b) b.offset.set(waterClock * -0.014, waterClock * 0.030);
}

/**
 * Hand the water the scene's prefiltered environment.
 *
 * It needs an envMap of its own rather than riding on scene.environment,
 * because the renderer overwrites the envMapIntensity uniform with
 * scene.environmentIntensity for every material whose own envMap is null.  On
 * the shared path the creek would therefore reflect at whatever strength the
 * street wants -- and the street wants about a third of it, or every roof and
 * lawn washes out.  With its own envMap the water keeps its full sky.
 */
export function useEnvironment(env) {
  MATS.water.envMap = env;
  MATS.water.needsUpdate = true;
}

export default MATS;
